import type { ItineraryLeg } from '@/services/routePlanner'

/** What a journey costs, once the offline dataset has been asked about each leg. */
export interface ItineraryFare {
  /**
   * Fare per leg in dong, index-aligned with the legs passed in. `undefined`
   * where the fare is not known — the row renders no price rather than `0 đ`.
   */
  perLeg: (number | undefined)[]
  /** Sum of the KNOWN leg fares. Meaningless unless `isKnown`. */
  total: number
  /** At least one leg priced, i.e. `total` is worth showing at all. */
  isKnown: boolean
  /** At least one leg unpriced, i.e. `total` is a lower bound. */
  isPartial: boolean
}

/**
 * Prices an itinerary from the offline route snapshot.
 *
 * Hanoi fares are **flat per boarding**, not per kilometre, so a journey costs the
 * sum of its legs' route fares and a transfer means a second full ticket. Nothing
 * here scales with distance on purpose.
 *
 * Two ways a leg can come back unpriced, and both must be treated the same:
 *
 * 1. `routeId` is absent from `routes.json` — the live planner returns ids that
 *    postdate the snapshot (observed: 4816 against a 164-route snapshot). This is
 *    the same case `ItineraryCard` already renders as `UNKNOWN_ROUTE`.
 * 2. The route is present but its fare is 0, which the extractor emits for a value
 *    it could not parse. 0 is *unknown*, never free — see `formatFare`.
 *
 * Pure and exported so the pricing rules can be tested without mounting the card.
 */
export function itineraryFare(
  legs: ItineraryLeg[],
  faresByRouteId: Map<number, number>,
): ItineraryFare {
  const perLeg = legs.map((leg) => {
    const fare = faresByRouteId.get(leg.routeId)
    return fare !== undefined && fare > 0 ? fare : undefined
  })

  const priced = perLeg.filter((fare): fare is number => fare !== undefined)

  return {
    perLeg,
    total: priced.reduce((sum, fare) => sum + fare, 0),
    isKnown: priced.length > 0,
    isPartial: priced.length < perLeg.length,
  }
}
